/* eslint-disable no-restricted-syntax */
import type { ImageResource } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-resource';
import BlobStorage, { BlobMetadata } from '@tencent/memoire-blob-storage';

interface ImageAdditions {
    id: string;
    quickLookUrl: string;
}

interface ImageItem {
    path: string;
    blob: Blob;
    metadata: BlobMetadata<ImageAdditions>;
}

class ImageRecoveryManager {
    private storage: BlobStorage;
    private enabled: boolean;

    public constructor(namespace: string[], { enabled = true }: { enabled?: boolean } = {}) {
        this.storage = new BlobStorage(namespace);
        this.enabled = enabled;
    }

    public async backupAll(resources: ImageResource[]): Promise<void[]> {
        return Promise.all(resources.map(resource => this.backup(resource)));
    }

    public async backup(resource: ImageResource): Promise<void> {
        if (!this.enabled) return;

        const url = resource.getQuickLookUrl();
        await this.storage.write(resource.id, resource.getBlob(), {
            additions: {
                id: resource.id,
                quickLookUrl: url,
            },
        });

        // 由于上传失败和备份写入时序是随机的，
        // 可能造成在上传失败删除备份的回调调用时，备份还未开始写入
        // 因此，备份完毕后，需要检查图片资源非网络原因（即不合法的图片）上传失败
        // 如果失败了，则需要删除本地备份，
        const err = resource.getLastUploadError();
        if (err && !err?.isNetworkError) {
            this.drop(resource.id);
        }
    }

    public async drop(id: string): Promise<string> {
        return this.storage.remove(id);
    }

    public async get(id: string): Promise<Blob | null> {
        return this.storage.readAsBlob(id);
    }

    public async recovery(): Promise<ImageItem[]> {
        const paths = await this.storage.list();
        const imageItems: ImageItem[] = [];
        for (const path of paths) {
            const blob = await this.storage.readAsBlob(path);
            const metadata = await this.storage.stat<ImageAdditions>(path);

            if (!blob || !metadata) {
                await this.drop(path);
            } else {
                imageItems.push({ path, blob, metadata });
            }
        }

        return imageItems;
    }
}

export default ImageRecoveryManager;
